import policyCompany from "./policy.company.mjs";

export const termsAndConditions = {
  title: "Terms & Conditions",
  effectiveDate: "20 September 2026",
  version: "1.0",
  company: policyCompany,
  summary: "Terms governing the use of DCAR and our vehicle-related services.",
  sections: [],
};

// Content supplied by DCAR; presentation follows the shared policy layout.
export default function TermsSections() {
  return <>
      <section className="policy-section">
        <p>
          These Terms &amp; Conditions (“Terms”) govern the use of
          <strong>www.dcar.my</strong>, the DCAR mobile web experience and the
          services provided by DCH Automobile Sdn Bhd (“DCAR”, “we”, “us”, “our”).
        </p>

        <p>
          By accessing our website, registering an account, making a vehicle
          reservation, purchasing a vehicle, consigning a vehicle or using any
          DCAR service, you confirm that you have read, understood and agreed to
          these Terms.
        </p>
      </section>

      <section className="policy-section">
        <h3>1. DCAR Services</h3>

        <p>DCAR provides vehicle-related services which may include:</p>

        <ul>
          <li>Vehicle listing and enquiry</li>
          <li>Vehicle reservation</li>
          <li>Vehicle inspection and condition reporting</li>
          <li>Vehicle purchase and sale facilitation</li>
          <li>Vehicle consignment</li>
          <li>Financing and insurance application assistance</li>
          <li>Ownership transfer coordination</li>
          <li>Vehicle viewing and handover</li>
          <li>Vehicle delivery or transportation arrangements</li>
          <li>Workshop, inspection and other related automotive services</li>
        </ul>

        <p>
          Service availability may vary depending on the vehicle, location and
          transaction.
        </p>

        <p>
          Users must be at least 18 years old and legally capable of entering
          into a binding agreement.
        </p>
      </section>

      <section className="policy-section">
        <h3>2. Vehicle Listings and Condition</h3>

        <p>
          DCAR takes reasonable steps to ensure that vehicle information displayed
          on the website is accurate.
        </p>

        <p>Vehicle listings may include information such as:</p>

        <ul>
          <li>Make and model</li>
          <li>Registration year</li>
          <li>Mileage</li>
          <li>Specifications</li>
          <li>Vehicle condition</li>
          <li>Photographs</li>
          <li>Inspection findings</li>
          <li>Selling price</li>
        </ul>

        <p>
          Information may be provided by the registered owner, DCAR, inspection
          partners or other relevant third parties.
        </p>

        <p>
          Used vehicles may show normal wear and tear based on their age, mileage
          and previous use.
        </p>

        <p>
          Inspection reports, vehicle grades and photographs describe the vehicle
          condition at the time of inspection and do not represent a guarantee
          that the vehicle is completely free from defects.
        </p>

        <p>
          Customers are encouraged to inspect the vehicle personally or appoint
          an independent inspector before completing the purchase.
        </p>
      </section>

      <section className="policy-section">
        <h3>3. Vehicle Reservation</h3>

        <p>
          A vehicle reservation temporarily holds a selected vehicle while the
          customer completes the required checks, financing and documentation.
        </p>

        <p>
          Unless otherwise displayed during checkout, the standard reservation
          fee is <strong>RM5,000 per vehicle</strong>.
        </p>

        <p>
          The reservation fee will be credited towards the vehicle purchase price
          if the customer proceeds with the purchase.
        </p>

        <p>
          A reservation does not by itself constitute a completed vehicle sale.
          The sale is considered confirmed only after the applicable Vehicle Sales
          Order or other transaction agreement has been accepted by the relevant
          parties.
        </p>

        <h4>Reservation Period</h4>

        <p>
          A standard reservation remains valid for
          <strong>14 calendar days</strong> from the date the reservation payment
          is received unless another period is stated in the reservation
          confirmation.
        </p>

        <p>
          An extension may be requested before the reservation expires and remains
          subject to DCAR's approval.
        </p>

        <h4>Refundable Reservation</h4>

        <p>The reservation fee may be refunded where, for example:</p>

        <ul>
          <li>The vehicle becomes unavailable</li>
          <li>The registered owner declines or cannot proceed with the sale</li>
          <li>DCAR cannot provide clear and transferable ownership</li>
          <li>
            A material difference is discovered between the vehicle and the
            published vehicle information
          </li>
          <li>
            The customer's financing application is rejected, subject to
            supporting documentation
          </li>
          <li>DCAR cancels the transaction</li>
          <li>
            The customer cancels within 24 hours of paying the reservation fee
            and has not signed the Vehicle Sales Order
          </li>
        </ul>

        <h4>Non-Refundable Reservation</h4>

        <p>The reservation fee may be forfeited where:</p>

        <ul>
          <li>
            The customer changes their mind after the applicable cancellation
            period
          </li>
          <li>
            The customer allows the reservation to expire without completing the
            required action
          </li>
          <li>
            Required documents are not submitted within the reservation period
          </li>
          <li>The customer fails to make the required payment</li>
          <li>False, incomplete or misleading information is provided</li>
          <li>
            The transaction cannot be completed due to a reason within the
            customer's control
          </li>
        </ul>
      </section>

      <section className="policy-section">
        <h3>4. Payment, Cancellation and Refund</h3>

        <p>
          All amounts displayed on DCAR are in Malaysian Ringgit (RM) unless
          otherwise stated.
        </p>

        <p>
          Payments must be made through payment methods supported by DCAR. A
          transaction will only be treated as paid after payment has been
          successfully received and confirmed.
        </p>

        <h4>Cancellation Before Vehicle Handover</h4>

        <p>
          A customer may request cancellation before completion of the transaction.
          The amount refundable will depend on the stage of the transaction and
          any applicable signed agreement.
        </p>

        <p>
          DCAR may deduct reasonable third-party costs already incurred at the
          customer's request, including where applicable:
        </p>

        <ul>
          <li>Vehicle transportation</li>
          <li>Specialist inspection</li>
          <li>Financing processing</li>
          <li>Insurance-related charges</li>
          <li>Ownership transfer-related costs</li>
          <li>Other approved third-party services</li>
        </ul>

        <p>
          Where deductions apply, DCAR will provide a breakdown.
        </p>

        <h4>Vehicle Returns</h4>

        <p>
          Vehicles sold through DCAR are registered motor vehicles and cannot
          generally be returned simply because the customer changes their mind
          after:
        </p>

        <ul>
          <li>Ownership transfer has been completed; or</li>
          <li>The vehicle has been handed over and the transaction completed.</li>
        </ul>

        <p>
          This does not affect any rights available to the customer under
          applicable Malaysian law or any written warranty provided for the
          vehicle.
        </p>

        <h4>Duplicate or Incorrect Payments</h4>

        <p>Customers should contact DCAR if they believe:</p>

        <ul>
          <li>They have been charged twice</li>
          <li>An incorrect amount has been charged</li>
          <li>Payment has been made to an incorrect DCAR transaction</li>
        </ul>

        <p>
          Relevant payment receipts and transaction details may be required for
          verification. Verified duplicate payments or overpayments will be
          refunded.
        </p>

        <h4>Refund Processing Time</h4>

        <p>
          Once a refund has been approved by DCAR, the refund will be processed
          within <strong>14 working days</strong>.
        </p>

        <p>
          The time taken for the refunded amount to appear in the customer's
          account may vary depending on the bank, card issuer, e-wallet provider
          or payment gateway.
        </p>

        <p>
          Refunds will normally be returned to the original payment method.
        </p>
      </section>

      <section className="policy-section">
        <h3>5. Vehicle Consignment</h3>

        <p>
          Vehicle owners may appoint DCAR to assist with marketing and selling
          their vehicles on consignment.
        </p>

        <p>By consigning a vehicle, the owner confirms that:</p>

        <ul>
          <li>They are the registered owner or an authorised representative</li>
          <li>The vehicle information provided is accurate</li>
          <li>Any outstanding financing has been disclosed</li>
          <li>
            Any known accident, flood, mechanical or ownership issue has been
            disclosed
          </li>
          <li>
            There is no undisclosed restriction preventing the vehicle from
            being sold
          </li>
        </ul>

        <p>DCAR may:</p>

        <ul>
          <li>Inspect the vehicle</li>
          <li>Photograph the vehicle</li>
          <li>Prepare and publish the vehicle listing</li>
          <li>Promote the vehicle</li>
          <li>Arrange customer enquiries</li>
          <li>Arrange vehicle viewings</li>
          <li>Arrange test drives</li>
          <li>Communicate buyer offers to the owner</li>
        </ul>

        <p>
          Any agreed consignment fee, commission, minimum selling price or other
          commercial terms may be stated separately in a Consignment Agreement.
        </p>

        <p>
          DCAR will not accept an offer below an agreed minimum selling price
          without the owner's approval unless previously authorised in writing.
        </p>

        <p>
          Where DCAR receives sale proceeds on behalf of an owner, DCAR may first
          deduct or settle agreed amounts such as:
        </p>

        <ul>
          <li>Outstanding vehicle financing</li>
          <li>Agreed commission</li>
          <li>Approved third-party expenses</li>
          <li>Other amounts authorised as part of the transaction</li>
        </ul>

        <p>
          The remaining proceeds will then be released to the owner in accordance
          with the transaction arrangement.
        </p>
      </section>

      <section className="policy-section">
        <h3>6. Financing, Insurance and Ownership Transfer</h3>

        <p>
          Where DCAR assists a customer with financing or motor insurance,
          applications are assessed by the relevant bank, financier or insurer.
        </p>

        <p>DCAR does not guarantee:</p>

        <ul>
          <li>Financing approval</li>
          <li>Financing amount</li>
          <li>Interest or profit rate</li>
          <li>Insurance approval</li>
          <li>Insurance premium</li>
          <li>Third-party processing time</li>
        </ul>

        <p>
          Vehicle handover may only proceed after all applicable requirements
          are completed, including where relevant:
        </p>

        <ul>
          <li>Full payment</li>
          <li>Financing approval and disbursement</li>
          <li>Existing financing settlement</li>
          <li>Motor insurance activation</li>
          <li>Required documentation</li>
          <li>Ownership transfer requirements</li>
          <li>JPJ or other regulatory requirements</li>
        </ul>
      </section>

      <section className="policy-section">
        <h3>7. Vehicle Handover, Service and Delivery</h3>

        <h4>Vehicle Handover ETA</h4>

        <p>
          Once all transaction requirements have been completed, DCAR will
          normally arrange vehicle handover within
          <strong>7 working days</strong>.
        </p>

        <p>
          The 7-working-day period begins only after all applicable requirements
          have been fulfilled, which may include:
        </p>

        <ul>
          <li>Full payment or financing disbursement</li>
          <li>Insurance activation</li>
          <li>Ownership transfer documentation</li>
          <li>Required transaction documents</li>
          <li>Existing vehicle loan settlement</li>
          <li>Any agreed vehicle preparation work</li>
        </ul>

        <h4>Vehicle Collection</h4>

        <p>
          Where the customer collects the vehicle directly, DCAR will notify the
          customer once the vehicle is ready.
        </p>

        <p>
          Customers may be required to provide identification and other relevant
          transaction documents before collection.
        </p>

        <h4>Vehicle Delivery</h4>

        <p>
          Where DCAR agrees to arrange vehicle delivery, delivery may be performed
          by DCAR or an appointed third-party transporter.
        </p>

        <p>
          An estimated delivery date will be provided once the delivery
          arrangement is confirmed.
        </p>

        <p>Delivery time may depend on:</p>

        <ul>
          <li>Pickup and delivery location</li>
          <li>Transport availability</li>
          <li>Vehicle type</li>
          <li>Weather</li>
          <li>Road conditions</li>
          <li>Public holidays</li>
          <li>Other operational factors</li>
        </ul>

        <p>
          Any applicable delivery or transportation fee will be disclosed before
          confirmation.
        </p>

        <h4>Service ETA</h4>

        <p>
          For inspection, workshop or other automotive services, the estimated
          completion date will be communicated when reasonably possible.
        </p>

        <p>Completion time may change if:</p>

        <ul>
          <li>Additional defects are discovered</li>
          <li>Parts must be ordered</li>
          <li>Additional customer approval is required</li>
          <li>Additional work is requested</li>
          <li>A third-party service provider experiences delays</li>
        </ul>
      </section>

      <section className="policy-section">
        <h3>8. Customer Responsibilities and Third-Party Services</h3>

        <p>Customers are responsible for:</p>

        <ul>
          <li>Providing accurate information</li>
          <li>Providing genuine supporting documents</li>
          <li>Responding to reasonable verification requests</li>
          <li>Making payment within the required period</li>
          <li>Reviewing transaction documents before signing</li>
          <li>Keeping account login details and OTP codes secure</li>
        </ul>

        <p>DCAR may use third-party providers including:</p>

        <ul>
          <li>Payment gateways</li>
          <li>Banks</li>
          <li>Financiers</li>
          <li>Insurance companies</li>
          <li>Inspection providers</li>
          <li>Workshops</li>
          <li>Vehicle transporters</li>
          <li>Government or regulatory services</li>
        </ul>

        <p>
          Third-party services may be subject to their own terms and processing
          times.
        </p>

        <p>
          DCAR is not responsible for delays or decisions made by independent
          third parties unless caused directly by DCAR.
        </p>
      </section>

      <section className="policy-section">
        <h3>9. Complaints, Dispute Resolution and Governing Law</h3>

        <p>
          Customers who have a complaint or dispute should contact DCAR first so
          that both parties can attempt to resolve the issue.
        </p>

        <p>Customers should provide relevant information such as:</p>

        <ul>
          <li>Full name</li>
          <li>Contact number</li>
          <li>Vehicle registration number or listing reference</li>
          <li>Reservation or transaction reference</li>
          <li>Description of the issue</li>
          <li>Relevant supporting documents</li>
        </ul>

        <p>
          DCAR and the customer will first attempt in good faith to resolve the
          dispute through discussion or written communication.
        </p>

        <p>
          If the dispute cannot be resolved amicably, either party may exercise
          the rights available under applicable Malaysian law, including referring
          the matter to a competent court, tribunal or other relevant authority.
        </p>

        <p>
          These Terms &amp; Conditions are governed by and interpreted in accordance
          with the <strong>laws of Malaysia</strong>.
        </p>

        <p>
          Nothing in these Terms limits any statutory consumer rights that cannot
          legally be excluded.
        </p>
      </section>

      <section className="policy-section">
        <h3>10. Privacy, Changes and Contact</h3>

        <p>
          Personal data collected through DCAR is handled in accordance with the
          DCAR Website Privacy Policy and the
          <strong>Personal Data Protection Act 2010 (PDPA)</strong>.
        </p>

        <p>
          Please refer to our
          <a href="/privacy-policy">Privacy Policy</a>
          for further information.
        </p>

        <p>
          DCAR may update these Terms from time to time to reflect changes to our
          services, business operations or applicable requirements.
        </p>

        <p>
          The latest version and effective date will be published on the website.
        </p>

        <h4>Contact Us</h4>

        <address>
          <strong>DCH Automobile Sdn Bhd</strong><br />
          No. 7, Jalan Astana F/KU2<br />
          Bandar Bukit Raja<br />
          41050 Klang, Selangor<br />
          Malaysia
        </address>

        <p>
          Email:
          <a href="mailto:support@dcar.my">support@dcar.my</a><br />
          Phone:
          <a href="tel:+6036666868">+03 666 6868</a>
        </p>
      </section>

  </>;
}
